import { computed, Injectable, signal } from '@angular/core';
import { GuideStep, GuideTask } from '../models/guide.model';

/** Which way the last step move went — the overlay uses this to decide whether
 *  to perform a step's action (forward) or just re-highlight it (backward). */
export type GuideNavDirection = 'forward' | 'backward';

/**
 * State machine that steps through a {@link GuideTask} and owns playback state.
 *
 * The `GuideOverlayComponent` reacts to `currentStep`, `isPaused` and
 * `navDirection` for visual playback / DOM actions; `GuideControls` drives
 * play/pause/next/previous/exit. The chat service reacts to
 * `lastCompletedTaskId` to narrate completion (a signal rather than an injected
 * back-reference, to avoid a DI cycle).
 *
 * Playback: `isPaused === false` is auto-play (the overlay performs each step
 * and auto-advances). The manual controls put playback into `isPaused === true`
 * (manual mode); Play resumes auto-play. They are mutually exclusive so a
 * step's action is never performed twice.
 */
@Injectable({ providedIn: 'root' })
export class GuideEngineService {
  private readonly activeTaskSignal = signal<GuideTask | null>(null);
  private readonly stepIndexSignal = signal(0);
  private readonly isPausedSignal = signal(false);
  private readonly navDirectionSignal = signal<GuideNavDirection>('forward');
  /** Bumped by `next()`; the overlay reacts to perform-then-advance. */
  private readonly nextTickSignal = signal(0);

  /** Bumped with the task id whenever a task completes (not on cancel). */
  readonly lastCompletedTaskId = signal<string | null>(null);

  readonly activeTask = this.activeTaskSignal.asReadonly();
  readonly stepIndex = this.stepIndexSignal.asReadonly();
  readonly isPaused = this.isPausedSignal.asReadonly();
  readonly navDirection = this.navDirectionSignal.asReadonly();
  readonly nextTick = this.nextTickSignal.asReadonly();

  readonly isActive = computed(() => this.activeTaskSignal() !== null);
  readonly totalSteps = computed(() => this.activeTaskSignal()?.steps.length ?? 0);

  readonly currentStep = computed<GuideStep | null>(() => {
    const task = this.activeTaskSignal();
    if (!task) {
      return null;
    }
    return task.steps[this.stepIndexSignal()] ?? null;
  });

  readonly canPrevious = computed(() => this.isActive() && this.stepIndexSignal() > 0);
  readonly canNext = computed(() => this.isActive());

  startTask(task: GuideTask): void {
    this.activeTaskSignal.set(task);
    this.stepIndexSignal.set(0);
    this.navDirectionSignal.set('forward');
    this.isPausedSignal.set(false);
  }

  advance(): void {
    const task = this.activeTaskSignal();
    if (!task) {
      return;
    }
    this.navDirectionSignal.set('forward');
    const next = this.stepIndexSignal() + 1;
    if (next >= task.steps.length) {
      this.complete();
      return;
    }
    this.stepIndexSignal.set(next);
  }

  /** Play/Pause toggle. */
  togglePlay(): void {
    this.isPausedSignal.update((paused) => !paused);
  }

  /**
   * Manual "next": pause auto-play and ask the overlay to perform the current
   * step then advance. Auto-pausing keeps manual and auto modes exclusive.
   */
  next(): void {
    if (!this.isActive()) {
      return;
    }
    this.navDirectionSignal.set('forward');
    this.isPausedSignal.set(true);
    this.nextTickSignal.update((tick) => tick + 1);
  }

  /** Manual "previous": re-highlight the earlier step for review (no undo). */
  previous(): void {
    if (this.stepIndexSignal() <= 0) {
      return;
    }
    this.navDirectionSignal.set('backward');
    this.isPausedSignal.set(true);
    this.stepIndexSignal.update((index) => index - 1);
  }

  /** End the walkthrough. */
  exit(): void {
    this.cancel();
  }

  cancel(): void {
    this.reset();
  }

  private complete(): void {
    const completedId = this.activeTaskSignal()?.id ?? null;
    this.reset();
    if (completedId) {
      this.lastCompletedTaskId.set(completedId);
    }
  }

  private reset(): void {
    this.activeTaskSignal.set(null);
    this.stepIndexSignal.set(0);
    this.navDirectionSignal.set('forward');
    this.isPausedSignal.set(false);
  }
}
